import Checkbox from "common/Checkbox/Checkbox";
import React, { ReactNode, useMemo, useState } from "react";
import {
  INACTIVE_GROUP_STATUSES,
  PENDING_GROUP_STATUSES,
  USER_MEMBERSHIP_ACTIVE_STATUS,
  USER_MEMBERSHIP_ADMIN_KICKED_STATUS,
  USER_MEMBERSHIP_BOT_KICKED_STATUS,
  USER_MEMBERSHIP_EXPIRED_IN_GROUP_STATUS,
  USER_MEMBERSHIP_EXPIRED_KICKED_STATUS,
  USER_MEMBERSHIP_UNAUTHORIZED_STATUS,
} from "common/constants";
import { green, orange, red, yellow } from "@ant-design/colors";
import { find, get } from "lodash";
import moment from "moment";
import { dateIsLifetime } from "@shared/utils/date";
import Text from "common/Typography/Text";
import Button from "common/Buttons/Button";
import styled, { css, useTheme } from "styled-components";
import Tag from "antd/es/tag";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { hexToRGB } from "@shared/utils/misc";
import {
  LICENSE_STATUSES,
  SubGroupUserMembership,
  LanguageNameSpaces,
} from "@shared/types";
import Flex from "common/Boxes/Flex.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import GroupSelect from "features/admin/group/GroupSelect.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import FiltersSelect from "common/Filters/common/FiltersSelect.tsx";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { X } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const FILTERS_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  minWidth: 0,
};

const useFilters = (
  users: SubGroupUserMembership[],
  selectedTelegramIds: number[],
  onAssignLicensesClick: () => void,
  onRemoveErrorFilterClick: (() => void) | undefined,
  allowLicense: boolean | undefined
) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [licenseStatusFilter, setLicenseStatusFilter] = useState(FILTER_ALL);
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [expirationFilter, setExpirationFilter] = useState<ExpirationFilters>(
    ExpirationFilters.FILTER_ALL
  );
  const [hideBots, setHideBots] = useState(true);

  const selectedFilterStatus = find(
    STATUSES_INFO_MAP,
    (statusesInfo) => statusesInfo.displayName === statusFilter
  );

  const filterUserByStatusFilter = (user: SubGroupUserMembership) => {
    const userStatusInfo = find(
      STATUSES_INFO_MAP,
      (statusesInfo) => statusesInfo.statuses.indexOf(user.status) > -1
    );
    return selectedFilterStatus
      ? selectedFilterStatus.displayName ===
          (userStatusInfo && userStatusInfo.displayName)
      : true;
  };

  const filterUserByLicenseStatusFilter = (user: SubGroupUserMembership) =>
    licenseStatusFilter === FILTER_ALL ||
    user?.user?.license_status === licenseStatusFilter;

  const filterUserByExpirationFilter = (user: SubGroupUserMembership) => {
    return EXPIRATION_FILTERS[expirationFilter](user);
  };

  const filterBotUsers = (user: SubGroupUserMembership) => {
    return hideBots ? !get(user, ["telegram_user", "is_bot"]) : true;
  };

  const filteredUsers = users
    .filter(filterUserByStatusFilter)
    .filter(filterUserByLicenseStatusFilter)
    .filter(filterUserByExpirationFilter)
    .filter(filterBotUsers);

  const statusFilterOptions = useMemo(() => {
    const options = STATUSES_INFO_MAP.map((statusInfo) => ({
      label: t(statusInfo.displayName),
      value: statusInfo.displayName,
    }));
    return [
      { label: t("users.filters.anyStatus"), value: FILTER_ALL },
      ...options,
    ];
  }, [i18n.language]);

  const expirationFilterOptions = useMemo(() => {
    const options = ExpirationFiltersSelect.map((expirationFilter) => ({
      label: t(expirationFilter),
      value: expirationFilter,
    }));
    return [
      {
        label: t("users.filters.anyExpiration"),
        value: ExpirationFilters.FILTER_ALL,
      },
      ...options,
    ];
  }, [i18n.language]);

  const licenseStatusFilterOptions = useMemo(() => {
    const options = LICENSE_STATUSES.map((status) => ({
      label: t(status),
      value: status,
    }));
    return [
      { label: t("users.filters.anyLicenseStatus"), value: FILTER_ALL },
      ...options,
    ];
  }, [i18n.language]);

  const commonSelectProps = {
    popupMatchSelectWidth: false,
    hideArrow: isBottomNavigator,
    $singleRow: isBottomNavigator,
    $isBottomNavigator: isBottomNavigator,
  };

  let component: ReactNode;
  if (allowLicense && selectedTelegramIds.length && appTheme.isNewLook) {
    // New look: the selection replaces the filters - the count, the one
    // action, and a quiet chip while only the failed rows are shown.
    component = (
      <>
        <NewLookSelected>
          {selectedTelegramIds.length} {t("users.filters.selected")}
        </NewLookSelected>
        <Button onClick={onAssignLicensesClick}>
          {t("users.filters.assignLicenses")}
        </Button>
        {onRemoveErrorFilterClick && (
          <NewLookErrorsChip>
            {t("users.filters.errors")}
            <button
              type="button"
              aria-label={t("users.filters.errors")}
              onClick={onRemoveErrorFilterClick}
            >
              <X size={12} strokeWidth={2} />
            </button>
          </NewLookErrorsChip>
        )}
      </>
    );
  } else if (allowLicense && selectedTelegramIds.length) {
    component = (
      <>
        <Text
          style={{ borderRight: appTheme.borders.medium, paddingRight: 12 }}
        >
          {selectedTelegramIds.length} {t("users.filters.selected")}
        </Text>
        <Button onClick={onAssignLicensesClick} style={{ marginLeft: 12 }}>
          {t("users.filters.assignLicenses")}
        </Button>
        {onRemoveErrorFilterClick && (
          <Tag
            closable={true}
            style={{
              borderRadius: 8,
              backgroundColor: hexToRGB(appTheme.colors.red, 0.3),
              color: appTheme.colors.red,
              border: appTheme.borders.light,
              padding: "4px 8px",
              marginLeft: 12,
            }}
            closeIcon={
              <CloseOutlined
                style={{ color: appTheme.colors.red, marginLeft: 4 }}
              />
            }
            onClose={onRemoveErrorFilterClick}
          >
            {t("users.filters.errors")}
          </Tag>
        )}
      </>
    );
  } else {
    component = (
      <FiltersWrapper>
        {!isBottomNavigator && (
          <Text style={{ marginRight: 8, whiteSpace: "nowrap" }}>
            {t("users.filters.filterBy")}
          </Text>
        )}
        {isBottomNavigator && (
          <GroupSelect style={{ marginRight: 8 }} size="middle" />
        )}
        <FiltersSelect
          value={statusFilter}
          onChange={(value: string) => setStatusFilter(value)}
          options={statusFilterOptions}
          mobileTitle={t("users.filters.statusLabel")}
          $allSelected={statusFilter === FILTER_ALL}
          {...commonSelectProps}
        />
        <FiltersSelect
          value={expirationFilter}
          style={{ marginLeft: 8 }}
          onChange={(value: ExpirationFilters) => setExpirationFilter(value)}
          options={expirationFilterOptions}
          mobileTitle={t("users.filters.expirationLabel")}
          $allSelected={expirationFilter === ExpirationFilters.FILTER_ALL}
          {...commonSelectProps}
        />
        {allowLicense && (
          <FiltersSelect
            value={licenseStatusFilter}
            style={{ marginLeft: 8 }}
            onChange={(value: string) => setLicenseStatusFilter(value)}
            options={licenseStatusFilterOptions}
            mobileTitle={t("users.filters.licenseStatusLabel")}
            $allSelected={licenseStatusFilter === FILTER_ALL}
            {...commonSelectProps}
          />
        )}
        <Checkbox
          onChange={(e: CheckboxChangeEvent) => setHideBots(e.target.checked)}
          checked={hideBots}
          style={{ marginLeft: 8 }}
        >
          <Text style={{ whiteSpace: "nowrap" }}>
            {t("users.filters.hideBots")}
          </Text>
        </Checkbox>
      </FiltersWrapper>
    );
  }

  component = <div style={FILTERS_ROW_STYLE}>{component}</div>;

  return { component, filteredUsers };
};

export default useFilters;

const FILTER_ALL = "all";

enum ExpirationFilters {
  NEXT_HOUR = "users.expiration.nextHour",
  NEXT_DAY = "users.expiration.nextDay",
  NEXT_WEEK = "users.expiration.nextWeek",
  NEXT_MONTH = "users.expiration.nextMonth",
  LIFETIME = "users.expiration.lifetime",
  NOT_SET = "users.expiration.notSet",
  FILTER_ALL = "all",
}
const ExpirationFiltersSelect = [
  ExpirationFilters.NEXT_HOUR,
  ExpirationFilters.NEXT_DAY,
  ExpirationFilters.NEXT_WEEK,
  ExpirationFilters.NEXT_MONTH,
  ExpirationFilters.LIFETIME,
  ExpirationFilters.NOT_SET,
] as const;

const EXPIRATION_FILTERS: {
  [key in ExpirationFilters]: (
    user: SubGroupUserMembership
  ) => boolean | string | undefined;
} = {
  [ExpirationFilters.NEXT_HOUR]: (user) => filterExpiration(user, 1, "hours"),
  [ExpirationFilters.NEXT_DAY]: (user) => filterExpiration(user, 1, "days"),
  [ExpirationFilters.NEXT_WEEK]: (user) => filterExpiration(user, 1, "weeks"),
  [ExpirationFilters.NEXT_MONTH]: (user) => filterExpiration(user, 1, "months"),
  [ExpirationFilters.LIFETIME]: (user) =>
    user.expiration_datetime && dateIsLifetime(user.expiration_datetime),
  [ExpirationFilters.NOT_SET]: (user) => !user.expiration_datetime,
  [ExpirationFilters.FILTER_ALL]: () => true,
};

export const STATUSES_INFO_MAP = [
  {
    statuses: [
      USER_MEMBERSHIP_ADMIN_KICKED_STATUS,
      USER_MEMBERSHIP_BOT_KICKED_STATUS,
    ] as string[],
    color: orange[5],
    displayName: "users.statuses.banned",
    sortValue: 1,
  },
  {
    statuses: [USER_MEMBERSHIP_EXPIRED_KICKED_STATUS] as string[],
    color: orange[5],
    displayName: "users.statuses.expiredBanned",
    sortValue: 2,
  },
  {
    statuses: INACTIVE_GROUP_STATUSES as string[],
    color: orange[5],
    displayName: "users.statuses.inactive",
    sortValue: 3,
  },
  {
    statuses: PENDING_GROUP_STATUSES as string[],
    color: yellow[5],
    displayName: "users.statuses.pending",
    sortValue: 4,
  },
  {
    statuses: [USER_MEMBERSHIP_ACTIVE_STATUS] as string[],
    color: green[5],
    displayName: "users.statuses.active",
    sortValue: 5,
  },
  {
    statuses: [USER_MEMBERSHIP_EXPIRED_IN_GROUP_STATUS] as string[],
    color: red[5],
    displayName: "users.statuses.expiredInGroup",
    sortValue: 6,
  },
  {
    statuses: [USER_MEMBERSHIP_UNAUTHORIZED_STATUS] as string[],
    color: red[5],
    displayName: "users.statuses.unauthorized",
    sortValue: 7,
  },
] as const;

export const filterExpiration = (
  user: SubGroupUserMembership,
  numTimeUnits: any,
  timeUnitStr: any
) => {
  if (!user.expiration_datetime) {
    return false;
  }
  const parsedUserExpiration = moment.utc(user.expiration_datetime);
  const parsedNow = moment.utc();
  const parsedExpirationCheck = parsedNow
    .clone()
    .add(numTimeUnits, timeUnitStr);
  return parsedUserExpiration.isBetween(parsedNow, parsedExpirationCheck);
};

const FiltersWrapper = styled(Flex)`
  min-width: 0;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: ${phoneMaxWidth}) {
    border-bottom: ${(p) => p.theme.borders.light};
    padding: 8px;
    ${(p) =>
      p.theme.isNewLook &&
      css`
        padding: 8px 16px;
        border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
      `}
  }
`;
const NewLookSelected = styled.span`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const NewLookErrorsChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 4px 0 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => hexToRGB(getNewLookTokens(p.theme).down, 0.1)};
  color: ${(p) => getNewLookTokens(p.theme).down};
  font-size: 12px;
  font-weight: 500;
  > button {
    display: inline-flex;
    padding: 2px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
`;
